import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { track } from '../utils/analytics';

// One concierge, one name — must match PERSONAS in the intake-agent Lambda.
// Rotating three names made the same widget introduce itself differently on consecutive
// loads, which reads as an unstable system on an intake surface (staging QA, 2026-09-16).
const PERSONA_NAME = 'Ava';

const QUICK_CHIPS = [
  'Invoices & data entry',
  'Lead follow-up',
  'Inbox triage',
  'Reporting',
  'Something else',
];

// Floating intake-chat widget. Opens from its own launcher bubble or when any part of
// the page dispatches the `openIntakeChat` event. Talks to the Bedrock agent behind the
// API Gateway endpoint published as custom.intakeAgentUrl in amplify_outputs.json.
// The persistent "AI concierge" header label is the bot disclosure — keep it visible.
// The model occasionally emits **bold** markdown; render it as <strong> instead of showing
// raw asterisks. Everything else stays plain text (whitespace-pre-wrap handles line breaks).
const renderInline = (text) => {
  const parts = String(text ?? '').split(/(\*\*[^*]+\*\*)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
      : part
  );
};

const ChatWidget = () => {
  const persona = PERSONA_NAME;
  const greeting = useMemo(
    () => ({
      role: 'agent',
      text: `Hi, I'm ${persona} with Automation Vita. What repetitive task is eating up your time — invoices, chasing leads, your inbox, scheduling?`,
    }),
    [persona]
  );

  const [open, setOpen] = useState(false);
  const [agentUrl, setAgentUrl] = useState('');
  const [messages, setMessages] = useState([greeting]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);
  const openedOnce = useRef(false);
  // Each funnel step fires once per page load. The agent keeps returning done=true on
  // every turn after the lead is captured, so without this guard one lead would be
  // counted once per follow-up message.
  const startedOnce = useRef(false);
  const leadTracked = useRef(false);

  const openChat = useCallback((source = 'launcher') => {
    setOpen(true);
    if (!openedOnce.current) {
      openedOnce.current = true;
      track('chat_open', { source });
    }
  }, []);

  const closeChat = useCallback(() => {
    setOpen(false);
    // Return focus to the launcher so keyboard users aren't dropped on <body>.
    setTimeout(() => launcherRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    fetch('/amplify_outputs.json')
      .then((r) => r.json())
      .then((o) => setAgentUrl(o?.custom?.intakeAgentUrl || ''))
      .catch(() => {});
    const openHandler = (e) => openChat(e?.detail?.source);
    window.addEventListener('openIntakeChat', openHandler);
    return () => window.removeEventListener('openIntakeChat', openHandler);
  }, [openChat]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    if (!agentUrl) {
      setMessages((m) => [...m, { role: 'user', text: trimmed }, {
        role: 'agent',
        text: "I'm not fully wired up yet — please email info@automationvita.com and we'll jump on it.",
      }]);
      setInput('');
      return;
    }
    setMessages((m) => [...m, { role: 'user', text: trimmed }]);
    setInput('');
    setLoading(true);
    if (!startedOnce.current) {
      startedOnce.current = true;
      track('chat_start');
    }
    track('chat_message');
    try {
      const res = await fetch(agentUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ message: trimmed, conversationId, persona, source: 'website' }),
      });
      const data = await res.json();
      if (data.conversationId) setConversationId(data.conversationId);
      setMessages((m) => {
        const next = [...m, { role: 'agent', text: data.reply || '…' }];
        if (data.blueprint) next.push({ role: 'agent', blueprint: true, text: data.blueprint });
        return next;
      });
      if (data.done && !leadTracked.current) {
        leadTracked.current = true;
        track('generate_lead', { method: 'chat' });
      }
    } catch (err) {
      setMessages((m) => [...m, {
        role: 'agent',
        text: "Sorry — I couldn't connect just then. You can email info@automationvita.com and we'll get right back to you.",
      }]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') closeChat();
  };

  const showChips = messages.length === 1 && !loading;

  return (
    <>
      <button
        ref={launcherRef}
        onClick={() => openChat('launcher')}
        aria-label={`Chat with ${persona}, our AI concierge`}
        className={`fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-brand-green hover:bg-brand-greenDark text-white shadow-lg items-center justify-center transition-all duration-300 hover:scale-105 ${open ? 'hidden' : 'flex'}`}
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Chat with ${persona}`}
          onKeyDown={onKeyDown}
          className="fixed z-50 inset-x-0 bottom-0 h-[88dvh] rounded-t-2xl sm:inset-x-auto sm:right-5 sm:bottom-5 sm:h-[32rem] sm:w-96 sm:max-h-[calc(100vh-2.5rem)] sm:rounded-2xl bg-white shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-brand-green text-white shrink-0">
            <div>
              <p className="font-display font-semibold text-sm leading-tight">{persona} · Automation Vita</p>
              <p className="text-xs text-white/85">AI concierge · every intake reviewed by Matthew</p>
            </div>
            <button
              onClick={closeChat}
              aria-label="Close chat"
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
            {messages.map((m, i) => (
              m.blueprint ? (
                <div key={i} className="border border-brand-green/40 rounded-2xl bg-white overflow-hidden">
                  <div className="bg-brand-green/10 px-4 py-2 font-display font-semibold text-sm text-brand-greenDark flex items-baseline justify-between gap-2">
                    <span>Your Automation Blueprint</span>
                    <span className="text-xs font-normal text-gray-500">first draft</span>
                  </div>
                  {/* Says on its face that a human has not checked it. The considered
                      version is the one Matthew sends; this is the two-minute sketch. */}
                  <p className="px-4 pt-3 text-xs leading-relaxed text-amber-800 bg-amber-50 border-b border-amber-100 pb-3 mx-0">
                    A rough draft, not a quote. Matthew reads every one and sends the worked-up
                    version within two business days.
                  </p>
                  <p className="px-4 py-3 text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">{renderInline(m.text)}</p>
                </div>
              ) : (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-brand-green text-white rounded-br-sm'
                        : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm'
                    }`}
                  >
                    {renderInline(m.text)}
                  </div>
                </div>
              )
            ))}
            {showChips && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    onClick={() => sendMessage(chip)}
                    className="px-3 py-1.5 rounded-full border border-brand-green/40 text-brand-greenDark text-sm bg-white hover:bg-brand-green hover:text-white transition-colors"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
                  <span className="inline-flex gap-1" aria-label={`${persona} is typing`}>
                    <span className="w-2 h-2 bg-gray-300 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-300 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-300 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 shrink-0 pb-[env(safe-area-inset-bottom)]">
            <form onSubmit={onSubmit} className="p-3 pb-1.5 flex gap-2">
              <label htmlFor="chat-input" className="sr-only">Your message</label>
              <input
                id="chat-input"
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Tell us what's eating your time…"
                autoComplete="off"
                className="flex-1 px-4 py-2.5 rounded-full border border-gray-200 text-sm text-gray-900 placeholder:text-gray-500 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green transition-colors"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                aria-label="Send message"
                className="w-10 h-10 shrink-0 rounded-full bg-brand-green hover:bg-brand-greenDark text-white flex items-center justify-center transition-all disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
            <p className="px-4 pb-2.5 text-[11px] leading-snug text-gray-500 text-center">
              Chats are recorded so we can scope your automation — please don&rsquo;t share
              sensitive details. <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">Privacy</a>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
